/**
 * PatientAppointmentsTabBody — tab 6 of the patient-edit shell (PROD-734).
 *
 * Flutter ref: `patient_appointments_tab.dart` (2029 lines) +
 * `patient_appointments_filters.dart`. Full parity surface:
 *   - speciality chips + provider/doctor facet cards (AppointmentProviderFilter)
 *   - Statuses popover + location icons + Filters drawer + active chips
 *     (AppointmentFilterBar)
 *   - 9-column table (leading flag/location icons → Date / Time / Doctor /
 *     Room / RFV / Status / Note → row-action kebab), client-side column sort
 *   - page-size selector + paging
 *   - row actions: Add Note / Edit / View Recurring / Cancel / View
 *
 * Wire: `POST /api/Appointment/ByPatientId` (paginated + `facets`). Speciality
 * is a CLIENT-side filter (Flutter `loadAppointmentList`); status / location /
 * practice / doctor / date go to the server and refetch.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, Flag, MessageSquare, Repeat } from 'lucide-react';
import { LOCATION_GLYPH, appointmentEdgeColor, type LocationType } from '@/scheduling/lib/appointment-glyphs';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { Banner, EmptyState } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { Select } from '@scripsteam/scrips-signal-ds';
import { usePermission } from '@/auth/permission-gate';
import { useAuthStore } from '@/auth/auth-store';
import { usePatientAppointments } from '@/patients/services/use-patient-appointments';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useAppointmentCancellationReasons } from '@/scheduling/services/use-appointment-cancellation-reasons';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { AppointmentProviderFilter } from './appointment-provider-filter';
import {
  AppointmentFilterBar,
  type AppointmentFilterState,
} from './appointment-filter-bar';
import { AppointmentStatusBadge } from './appointment-status-badge';
import { PatientAppointmentRowActions } from './patient-appointment-row-actions';
import type {
  AppointmentFacetPractice,
  AppointmentFacetProvider,
  ListAppointmentResponse,
} from '@/types/scheduling-api';

/** Flutter `app_constants.dart:724` doctorRole = "3". */
const DOCTOR_ROLE = '3';
const PAGE_SIZE_OPTIONS = [10, 20, 50];

type SortKey = 'date' | 'time' | 'doctor' | 'room' | 'rfv' | 'status' | 'note';
type SortDir = 'asc' | 'desc';

const EMPTY_FILTERS: AppointmentFilterState = {
  status: [],
  location: [],
  practices: [],
  practitioners: [],
  startDate: null,
  endDate: null,
};

export interface PatientAppointmentsTabBodyProps {
  patientId: string | null | undefined;
}

export function PatientAppointmentsTabBody({
  patientId,
}: PatientAppointmentsTabBodyProps) {
  const user = useAuthStore((s) => s.user);
  const canView = usePermission(['Manage_Patient_Appointments'], {
    view: true,
  });
  const canEdit = usePermission(['Manage_Patient_Appointments'], {
    edit: true,
  });

  const [filters, setFilters] = useState<AppointmentFilterState>(() => ({
    ...EMPTY_FILTERS,
    // Flutter seed: a doctor defaults to their own appointments.
    practitioners:
      user?.role === DOCTOR_ROLE && user?.practitionerId
        ? [user.practitionerId]
        : [],
  }));
  const [selectedSpeciality, setSelectedSpeciality] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const queryClient = useQueryClient();

  const { data, isFetching, isError, refetch } = usePatientAppointments({
    patientId,
    pageNumber: page,
    pageSize,
    status: filters.status,
    location: filters.location,
    practices: filters.practices,
    practitioners: filters.practitioners,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  const statusList = useStatusList().data ?? [];
  const cancellationReasons = useAppointmentCancellationReasons().data ?? [];
  const cancelledStatus = statusList.find((s) => s.code === 'Cancelled');
  // Org locations come from their own endpoint — NOT `user.organizationLocations`
  // (absent from the GetUserDataToken profile; Flutter stitches it on post-login
  // via GET /api/Organization/{org}/Locations). Same source the calendar uses.
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const { data: orgLocations = [] } = useOrgLocations(orgId);

  const totalPages = data?.totalPage ?? 1;
  const totalRecords = data?.count ?? 0;

  // Stable facet snapshot. The backend RE-COMPUTES `facets.provider` /
  // `facets.practice` from the *filtered* request — selecting one provider
  // collapses the facet list to just that provider. If the cards were bound
  // to `data.facets`, every other card would vanish on first select and the
  // user could never pick a second provider (the live Flutter bug). So we
  // capture the FULL facet set once (the initial unfiltered load) and keep the
  // cards / drawer / speciality chips bound to that snapshot.
  const [facetSnapshot, setFacetSnapshot] = useState<{
    providers: AppointmentFacetProvider[];
    practices: AppointmentFacetPractice[];
  } | null>(null);

  // Seed-once effect. Two jobs, both keyed to the FIRST non-empty facet
  // payload for a patient (NOT component mount):
  //
  //  (a) Snapshot the full provider/practice lists (see race guard below).
  //  (b) Seed `practitioners` with every provider id so the backend excludes
  //      appointments with NO provider assigned — the empty-GUID Provider
  //      participant (`reference: "0000…"`, `provider: null`). Verified live:
  //      count drops 896→874. Same mechanism as the calendar tab
  //      (`calendar_tab.dart:1461`).
  //
  // ⚠️ WORKAROUND / divergence note (review #1): this is NOT a cosmetic
  // client filter — it relies on the backend's `practitioners` clause to drop
  // the empty-GUID rows. If the backend EVER stops returning provider-less
  // occurrences (the real fix), this seeding becomes a harmless no-op (it
  // just filters to providers that all exist). The only failure mode is the
  // inverse: if `facets.provider` came back empty we skip the seed, and
  // provider-less rows would reappear — acceptable + visible, not silent.
  // DO NOT delete this thinking it's redundant: without it the tab shows the
  // unassigned recurring series as look-alike duplicate dates (see PR #413).
  //
  // RACE GUARD (review #4): `provs.length === 0` returns early, so the
  // snapshot is only ever taken AFTER the first response with real facets —
  // never on mount with `data === undefined`. Until then the render falls
  // back to live `data.facets` (see `providers`/`practices` below), so the
  // pinned list can never be empty and the cards always render.
  //
  // Runs once per patient; never overrides a non-empty selection (a doctor's
  // own-id seed or a manual pick).
  const seededPatientRef = useRef<string | null>(null);
  useEffect(() => {
    const provs = data?.facets?.provider;
    if (!provs || provs.length === 0) return; // wait for real facets (race guard)
    if (seededPatientRef.current === patientId) return;
    seededPatientRef.current = patientId ?? null;
    setFacetSnapshot({ providers: provs, practices: data?.facets?.practice ?? [] });
    if (filters.practitioners.length === 0) {
      setFilters((f) => ({ ...f, practitioners: provs.map((p) => p.id) }));
    }
  }, [
    data?.facets?.provider,
    data?.facets?.practice,
    patientId,
    filters.practitioners.length,
  ]);

  // Cards/drawer/chips bind to the snapshot (fallback to the live facets only
  // until the snapshot is captured on first load).
  const providers = facetSnapshot?.providers ?? data?.facets?.provider ?? [];
  const practices = facetSnapshot?.practices ?? data?.facets?.practice ?? [];

  // Speciality narrows the visible rows CLIENT-side (Flutter loadAppointmentList);
  // provider-less rows are already excluded server-side via `practitioners`.
  const rows = useMemo(() => {
    const all = data?.appointments ?? [];
    const filtered = selectedSpeciality
      ? all.filter((r) => r.specialityId === selectedSpeciality)
      : all;
    return sortRows(filtered, sortKey, sortDir);
  }, [data?.appointments, selectedSpeciality, sortKey, sortDir]);

  const onFiltersChange = (next: AppointmentFilterState) => {
    setPage(1);
    setFilters(next);
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const onChanged = () =>
    queryClient.invalidateQueries({ queryKey: ['patient-appointments'] });

  if (!patientId) {
    return (
      <EmptyState icon="info" title="Save the patient first to see their appointments." />
    );
  }

  return (
    <div className="space-y-4 p-6">
      <AppointmentProviderFilter
        providers={providers}
        selectedSpeciality={selectedSpeciality}
        onSelectSpeciality={setSelectedSpeciality}
        selectedPractitioners={filters.practitioners}
        onTogglePractitioner={(id) =>
          onFiltersChange({
            ...filters,
            practitioners: filters.practitioners.includes(id)
              ? filters.practitioners.filter((x) => x !== id)
              : [...filters.practitioners, id],
          })
        }
      />

      <AppointmentFilterBar
        filters={filters}
        statusList={statusList}
        locations={orgLocations}
        practices={practices}
        providers={providers}
        onChange={onFiltersChange}
      />

      {/* Export 21-10 (PaAppointmentsTab): a failed read is the error Banner with Try again (the query's refetch); it
          was never drawn, so a failure read as "No appointments". */}
      {isError && !isFetching ? (
        <Banner
          variant="error"
          title="Couldn't load appointments"
          action={
            <Button type="button" size="sm" variant="secondary" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        >
          Refresh the list to try again.
        </Banner>
      ) : isFetching && (data?.appointments?.length ?? 0) === 0 ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon="calendar" title="No appointments for this patient." />
      ) : (
        <div className="chart-plain-table" data-slot="record-appointments-table">
          <table className="w-full">
            <thead>
              <tr>
                <Th />
                <SortableTh
                  label="Date"
                  active={sortKey === 'date'}
                  dir={sortDir}
                  onClick={() => toggleSort('date')}
                />
                <SortableTh
                  label="Time"
                  active={sortKey === 'time'}
                  dir={sortDir}
                  onClick={() => toggleSort('time')}
                />
                <SortableTh
                  label="Doctor"
                  active={sortKey === 'doctor'}
                  dir={sortDir}
                  onClick={() => toggleSort('doctor')}
                />
                <SortableTh
                  label="Room"
                  active={sortKey === 'room'}
                  dir={sortDir}
                  onClick={() => toggleSort('room')}
                />
                <SortableTh
                  label="RFV"
                  active={sortKey === 'rfv'}
                  dir={sortDir}
                  onClick={() => toggleSort('rfv')}
                />
                <SortableTh
                  label="Status"
                  active={sortKey === 'status'}
                  dir={sortDir}
                  onClick={() => toggleSort('status')}
                />
                <SortableTh
                  label="Note"
                  active={sortKey === 'note'}
                  dir={sortDir}
                  onClick={() => toggleSort('note')}
                />
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <LeadingIcons row={row} />
                  </Td>
                  <Td>{formatDate(row.start)}</Td>
                  <Td>{formatTimeRange(row.start, row.end)}</Td>
                  <Td>
                    <DoctorCell row={row} />
                  </Td>
                  <Td>{row.room?.name ?? '—'}</Td>
                  <Td>
                    <span
                      className="line-clamp-1 inline-block max-w-35"
                      title={row.reasonCode?.display ?? undefined}
                    >
                      {row.reasonCode?.display ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <AppointmentStatusBadge status={row.status?.display ?? null} />
                  </Td>
                  <Td>
                    <span
                      className={`line-clamp-1 inline-block max-w-50 ${
                        row.comment
                          ? 'text-status-error'
                          : 'text-text-secondary'
                      }`}
                      title={row.comment ?? row.description ?? undefined}
                    >
                      {row.comment ?? row.description ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <PatientAppointmentRowActions
                      row={row}
                      canView={canView}
                      canEdit={canEdit}
                      currentUserId={user?.userId ?? ''}
                      cancellationReasons={cancellationReasons}
                      statusList={statusList}
                      cancelledStatus={cancelledStatus}
                      onChanged={onChanged}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="appt-foot">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <Select
            value={String(pageSize)}
            onChange={(v) => {
              setPage(1);
              setPageSize(Number(v));
            }}
            size="compact"
            className="w-18"
            options={PAGE_SIZE_OPTIONS.map((n) => ({
              label: String(n),
              value: String(n),
            }))}
          />
        </div>
        <div className="flex items-center gap-3">
          <span>
            {totalRecords > 0
              ? `Page ${page} of ${totalPages} (${totalRecords} total)`
              : 'No appointments'}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || isFetching}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th>{children}</th>
  );
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <th>
      <button type="button" onClick={onClick} className="appt-sort">
        {label}
        {active ? (
          dir === 'asc' ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />
        ) : null}
      </button>
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td>{children}</td>;
}

/** Leading flag/location-icon cell — Flutter `displayAppointmentLocationFlagsIcon`:
 *  profile color bar → location-type glyph → recurring → comment → flag →
 *  missing-data alert, each with a hover tooltip. */
function LeadingIcons({ row }: { row: ListAppointmentResponse }) {
  // Practice's rule (15 Oct, "comply with PM app", Antonin 16 Oct): the profile's colour is the slim leading bar, on
  // the muted ramp at 500; the location mark itself is neutral, in the meta ink (Practice's Day list rows).
  const locName = row.location?.name ?? '';
  const color = appointmentEdgeColor(row.appointmentProfile?.color, locName);
  const LocIcon = LOCATION_GLYPH[locName.toLowerCase() as LocationType] ?? LOCATION_GLYPH.block;
  const isRecurring = !!row.recurringId && !row.isBlock;
  const comment = row.comment ?? '';
  const flags = row.flagList ?? [];

  // Missing-data alert (Flutter parity). Patient fields beyond the typed
  // shape are read defensively (the wire ships them on the participant).
  const patient = findPatientParticipant(row.participant)?.patient as
    | (typeof row.participant[number]['patient'] & {
        age?: string;
        identificationId?: string;
        isInsuranceAdded?: boolean;
        guardianDetails?: { guaardianIdentificationId?: string } | null;
      })
    | undefined;
  const age = parseInt((patient?.age ?? '').replace(/y.*/, ''), 10) || 0;
  const needsGuardianId =
    age < 18 &&
    (!patient?.guardianDetails ||
      !(patient.guardianDetails.guaardianIdentificationId ?? '').length);
  const needsPatientId = age >= 18 && !(patient?.identificationId ?? '').length;
  const needsInsurance = patient?.isInsuranceAdded === false;
  const missing = [
    needsGuardianId ? "Add Guardian's ID" : '',
    needsPatientId ? "Add Patient's ID" : '',
    needsInsurance ? 'Add Insurance' : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block h-4 w-0.5 rounded-full bg-[var(--pa-profile-color)]"
        // `color` is a per-appointment value from the API (appointmentProfile.color) —
        // runtime, so it rides a CSS custom property rather than a hardcoded class.
        style={{ '--pa-profile-color': color } as CSSProperties}
        aria-hidden
      />
      <LocIcon className="size-4 text-text-muted" aria-label={locName} />
      {isRecurring ? (
        <Repeat className="size-4 text-text-secondary" role="img" aria-label="Recurring Appointment" />
      ) : null}
      {comment ? (
        <span title={comment} className="inline-flex">
          <MessageSquare className="size-4 text-text-secondary" aria-hidden />
        </span>
      ) : null}
      {flags.length > 0 ? (
        <span
          title={flags.map((f) => f.display).join(', ')}
          className="inline-flex"
        >
          <Flag className="size-4 text-text-secondary" aria-hidden />
        </span>
      ) : null}
      {missing ? (
        <span title={missing} className="inline-flex">
          <AlertTriangle className="size-4 text-status-warning" aria-hidden />
        </span>
      ) : null}
    </div>
  );
}

export function DoctorCell({ row }: { row: ListAppointmentResponse }) {
  const provider = row.participant?.find(
    (p) =>
      typeof p.referenceType === 'string' &&
      p.referenceType.toLowerCase() === 'provider',
  )?.provider;
  const name =
    provider?.name ||
    [provider?.firstName, provider?.lastName].filter(Boolean).join(' ');
  if (!name) return <span>—</span>;
  return (
    <div className="flex items-center gap-2">
      <Avatar
        size="2xs"
        src={provider?.photo || undefined}
        name={name}
        initials={name.slice(0, 1)}
      />
      <div className="min-w-0">
        <div className="truncate font-semibold text-text-primary">{name}</div>
        {row.specialty?.code?.displayName ? (
          <div className="truncate text-xs text-text-muted">
            {row.specialty.code.displayName}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function sortRows(
  rows: ListAppointmentResponse[],
  key: SortKey,
  dir: SortDir,
): ListAppointmentResponse[] {
  const factor = dir === 'asc' ? 1 : -1;
  const val = (r: ListAppointmentResponse): string => {
    switch (key) {
      case 'date':
        return r.start ?? '';
      case 'time':
        // Time-of-day component; same chronological order within a day.
        return (r.start ?? '').slice(11);
      case 'note':
        return r.comment ?? '';
      case 'doctor': {
        const prov = r.participant?.find(
          (p) => p.referenceType?.toLowerCase() === 'provider',
        )?.provider;
        return prov?.name ?? '';
      }
      case 'room':
        return r.room?.name ?? '';
      case 'rfv':
        return r.reasonCode?.display ?? '';
      case 'status':
        return r.status?.display ?? '';
      default:
        return '';
    }
  };
  return [...rows].sort((a, b) => factor * val(a).localeCompare(val(b)));
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const s = formatTime(start);
  const e = formatTime(end);
  if (s === '—' && e === '—') return '—';
  return `${s} – ${e}`;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
